import { Alert, AlertDescription } from '@/components/ui/alert';
import { trpc } from 'src/trpc';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { MoneyFromDollars, Money } from '@libs/services/money';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableFooter,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Form } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { NumericFormat } from 'react-number-format';
import { Switch } from '@/components/ui/switch';
import { useToast } from 'src/components/useToast';

import { AdditionalLineItem } from '@libs/schemas/src/order';

interface AdditionalLineItemDialogProps {
  open: boolean;
  onClose: () => void;
  orderTotal: Money;
  orderId: string;
  showTotalTable: boolean;
  lineItems: AdditionalLineItem[];
}

const LineItemPreview = ({
  existingLineItems,
  showTotalTable,
  removeLineItem,
}: {
  existingLineItems: AdditionalLineItem[];
  showTotalTable: boolean;
  total: Money;
  removeLineItem: (idx: number) => void;
}) => {
  const renderExistingLineItems = () => {
    return existingLineItems.map((lineItem, index) => (
      <TableRow>
        <TableCell className="w-1/2">
          {lineItem.label}
          <Button onClick={() => removeLineItem(index)} variant="link" className="text-red-400">
            Remove
          </Button>
        </TableCell>
        <TableCell colSpan={1} className="w-1/4 text-right">
          {lineItem.postTax ? 'No' : 'Yes'}
        </TableCell>
        <TableCell colSpan={2} className="w-1/4 text-right">
          {MoneyFromDollars(lineItem.amount).toFormat()}
        </TableCell>
      </TableRow>
    ));
  };
  if (!existingLineItems.length) {
    return;
  }
  return (
    <Table className="pb-0 mb-0 border-b-1-gray">
      <TableHeader>
        <TableRow>
          <TableHead className="w-1/3 font-bold">Label</TableHead>
          <TableHead className="w-2/3 font-bold text-right">Taxable</TableHead>
          <TableHead className="w-2/3 font-bold text-right">Amount</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {renderExistingLineItems()}
        {existingLineItems.length === 0 ? (
          <TableCell colSpan={3}>No additional line items</TableCell>
        ) : null}
      </TableBody>
      {showTotalTable ? (
        <TableFooter>
          <TableRow>
            <TableCell className="font-bold" colSpan={3}>
              Total
            </TableCell>
            <TableCell className="text-right font-bold"></TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={4}>
              Note: The total does not include material, dumpster, or weight adjustments. Taxes are
              also excluded.
            </TableCell>
          </TableRow>
        </TableFooter>
      ) : null}
    </Table>
  );
};

export const AdditionalLineItemDialog = ({
  open,
  onClose,
  orderId,
  lineItems,
}: AdditionalLineItemDialogProps) => {
  const [error, setError] = useState<null | string>(null);
  const utils = trpc.useUtils();
  const { mutateAsync, isPending: addLineItemsLoading } =
    trpc.admin.order.adminAddLineItemsToOrder.useMutation({
      onSuccess: () => {
        utils.admin.order.adminGetOrderRelatedInfoById.invalidate(orderId!);
      },
      onError() {
        setError('Unable to add line items to order.');
      },
    });

  const { successToast } = useToast();

  const form = useForm({
    resolver: zodResolver(
      z.object({
        label: z.string().min(1),
        amount: z.number().gt(0).multipleOf(0.01),
        postTax: z.boolean().default(false),
      }),
    ),
    mode: 'onSubmit', // Only validate on submit to avoid triggering validation on mount
    values: {
      label: '',
      amount: 1,
      postTax: false,
    },
  });

  // Watch form fields to enable/disable button without triggering validation
  const labelValue = form.watch('label');
  const amountValue = form.watch('amount');

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        onClose();
        form.reset();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Additional Charges</DialogTitle>
          <DialogDescription className="pt-1">
            Assign custom charges to the order.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <LineItemPreview
            existingLineItems={lineItems}
            removeLineItem={async (idx: number) => {
              const updatedLineItems = lineItems.filter((_, i) => i !== idx);

              // Filter out any invalid line items before sending to backend
              const validLineItems = updatedLineItems.filter(
                (item) =>
                  typeof item.label === 'string' &&
                  item.label.trim().length > 0 &&
                  typeof item.amount === 'number' &&
                  item.amount > 0 &&
                  typeof item.postTax === 'boolean',
              );

              await mutateAsync({
                orderId,
                lineItems: validLineItems,
              });

              successToast({ description: 'Additional charge successfully removed.' });
            }}
            total={MoneyFromDollars(1)}
            showTotalTable={false}
          />
          <div className="grid grid-cols-4 items-center gap-2">
            <Input
              placeholder="Label"
              id="label"
              defaultValue=""
              className="col-span-2"
              {...form.register('label')}
            />
            <NumericFormat
              displayType={'input'}
              thousandSeparator={true}
              decimalScale={2}
              className="col-span-1"
              prefix={'$'}
              onValueChange={(values) => {
                form.setValue('amount', parseFloat(values.value));
                form.trigger();
              }}
              customInput={Input}
              placeholder="$100.00"
            />
            <label htmlFor="taxable" className="col-span-1 pt-1">
              <Switch
                defaultChecked
                id="taxable"
                {...form.register('postTax')}
                onCheckedChange={(e) => {
                  form.setValue('postTax', !e);
                }}
              />
              <small className="align-top leading-6 font-bold pl-1">Taxable?</small>
            </label>
          </div>
          <DialogFooter className="pt-5">
            <Button
              onClick={form.handleSubmit(async (data) => {
                const updatedLineItems = [...lineItems, data];

                // Filter out any invalid line items before sending to backend
                const validLineItems = updatedLineItems.filter(
                  (item) =>
                    typeof item.label === 'string' &&
                    item.label.trim().length > 0 &&
                    typeof item.amount === 'number' &&
                    item.amount > 0 &&
                    typeof item.postTax === 'boolean',
                );

                return mutateAsync({
                  orderId,
                  lineItems: validLineItems,
                }).then(() => {
                  successToast({ description: `Added new '${data.label}' charge.` });
                  onClose();
                });
              })}
              disabled={!labelValue || !amountValue || amountValue <= 0 || addLineItemsLoading}
              type="submit"
            >
              Save
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
